import React, { useEffect } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowRight, X } from 'lucide-react';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 1.05rem;
  color: var(--color-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0 0 14px;
  font-size: 0.92rem;
  color: var(--black);
`;

const DepBlock = styled.div`
  border: 1px solid var(--shadow-a08);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
`;

const DepHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 12px;
`;

const Count = styled.div`
  font-weight: 700;
  color: var(--black);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  background: var(--danger-a10);
  color: var(--color-danger);
  font-size: 0.78rem;
  font-weight: 800;
  border-radius: 999px;
  padding: 2px 10px;
  min-width: 26px;
  text-align: center;
`;

const DepLink = styled.a`
  color: var(--green-800);
  font-weight: 700;
  font-size: 0.82rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; border-radius: 4px; }
`;

const DepList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: var(--ink-a60);
  font-size: 0.85rem;

  li + li { margin-top: 2px; }
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

const BtnSecondary = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--shadow-a08);
  border-radius: 8px;
  background: var(--white);
  color: var(--black);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--grey-100); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

export default function DependentsBlockDialog({
  open,
  customerName,
  scope = 'customer', // 'customer' | 'destination' | 'casetag' | 'pid'
  destinationName,
  subjectName, // used by 'casetag' scope (the case tag name)
  dependents,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !dependents) return null;

  const heading =
    scope === 'pid' ? `Can't inactivate “${subjectName || 'this item'}”`
    : scope === 'casetag' ? `Can't delete case tag "${subjectName || ''}"`
    : scope === 'destination' ? `Can't delete destination "${destinationName || ''}"`
    : `Can't delete ${customerName || ''}`;

  const intro =
    scope === 'pid' ? 'This item is used by active Customer Specs. Detach or inactivate those specs first, then you can set it Inactive.'
    : scope === 'casetag' ? 'This case tag is linked to active records. Resolve them before deleting.'
    : scope === 'destination' ? 'This destination is linked to active records. Resolve them before deleting.'
    : 'This customer is linked to active records. Resolve them before deleting.';

  const specsCount = dependents.specs?.count || 0;
  const tagsCount = dependents.caseTags?.count || 0;

  // Deep-link query params include the customer name so the target page can
  // optionally use it for filtering. If a target doesn't recognise the param,
  // it just lands on the index page — still useful.
  const customerParam = customerName ? `?customer=${encodeURIComponent(customerName)}` : '';
  const specsHref = `/CustomerSpecs/Index${customerParam}`;
  const tagsHref = `/case-tags${customerParam}`;

  return (
    <Backdrop onClick={onClose} role="presentation">
      <Dialog
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="block-dialog-title"
      >
        <CloseBtn type="button" onClick={onClose} aria-label="Close dialog">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="block-dialog-title">
          <AlertCircle size={18} aria-hidden="true" />
          {heading}
        </Title>
        <Body>{intro}</Body>

        {specsCount > 0 && (
          <DepBlock>
            <DepHead>
              <Count>
                <Badge>{specsCount}</Badge>
                Customer Specification{specsCount === 1 ? '' : 's'}
              </Count>
              <DepLink href={specsHref}>
                Open in Specs <ArrowRight size={12} aria-hidden="true" />
              </DepLink>
            </DepHead>
            {dependents.specs.sample?.length > 0 && (
              <DepList>
                {dependents.specs.sample.map((s) => (
                  <li key={`s-${s.id}`}>{s.name || `Spec #${s.id}`}</li>
                ))}
                {dependents.specs.hasMore && <li>…and more</li>}
              </DepList>
            )}
          </DepBlock>
        )}

        {tagsCount > 0 && (
          <DepBlock>
            <DepHead>
              <Count>
                <Badge>{tagsCount}</Badge>
                Case Tag{tagsCount === 1 ? '' : 's'}
              </Count>
              <DepLink href={tagsHref}>
                Open in Case Tags <ArrowRight size={12} aria-hidden="true" />
              </DepLink>
            </DepHead>
            {dependents.caseTags.sample?.length > 0 && (
              <DepList>
                {dependents.caseTags.sample.map((t) => (
                  <li key={`t-${t.id}`}>{t.name || `Tag #${t.id}`}</li>
                ))}
                {dependents.caseTags.hasMore && <li>…and more</li>}
              </DepList>
            )}
          </DepBlock>
        )}

        <Foot>
          <BtnSecondary type="button" onClick={onClose}>Cancel</BtnSecondary>
        </Foot>
      </Dialog>
    </Backdrop>
  );
}
